import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Card, OtpInput } from '@cowoo/shared/ui-web';
import { authErrorKey, handleAuthResult, mapAuthError, otpSchema, type AuthErrorKind } from '@cowoo/shared';
import { auth } from '../../lib/auth';
import { afterAuthentication } from './afterAuth';
import { readPendingLogin } from './pending';
import { AuthLayout } from './AuthLayout';

/**
 * `/login/challenge` (10-web-admin § 2, flow as 06 § 5.3).
 *
 * With `fingerprintEnforcement = off` (A-035) this should never be reached, so
 * it is deliberately the plainest of the three: a generic code input that gets
 * the user through if OtterAuth ever does ask, instead of a crash. A failure
 * goes back to `/login` rather than looping here.
 */
export function LoginChallenge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detour = readPendingLogin()?.detour;

  const [code, setCode] = useState('');
  const [error, setError] = useState<AuthErrorKind | null>(null);
  const [verifying, setVerifying] = useState(false);
  // `afterAuthentication` clears the pending login; without this the guard
  // below would bounce a signed-in user back to `/login`.
  const succeeded = useRef(false);

  useEffect(() => {
    if (succeeded.current) return;
    if (!detour || detour.kind !== 'challenge') navigate('/login', { replace: true });
  }, [detour, navigate]);

  async function verify(entered: string): Promise<void> {
    if (!detour || verifying) return;
    if (!otpSchema.safeParse(entered).success) {
      setError('otp_invalid');
      return;
    }

    setVerifying(true);
    setError(null);
    try {
      const state = handleAuthResult(await auth.submitCode(detour, entered));
      if (state.status === 'authenticated') {
        succeeded.current = true;
        navigate(afterAuthentication(state), { replace: true });
        return;
      }
      navigate('/login?error=oauth', { replace: true });
    } catch (thrown) {
      setError(mapAuthError(thrown, 'otp'));
      setCode('');
    } finally {
      setVerifying(false);
    }
  }

  const key = error ? authErrorKey(error) : null;

  return (
    <AuthLayout title={t('member.challenge.title')} body={t('member.challenge.body')}>
      <Card>
        <div className="flex flex-col gap-4">
          <OtpInput
            label={t('member.challenge.code')}
            value={code}
            onChange={setCode}
            onComplete={(entered) => void verify(entered)}
            error={key ? t(key) : null}
            disabled={verifying}
          />
          <Button onClick={() => void verify(code)} loading={verifying} disabled={code.length < 6} full>
            {t('member.challenge.submit')}
          </Button>
        </div>
      </Card>
    </AuthLayout>
  );
}
